import { Visitor } from '~/supabase/types'
import { useGetAgeGroups } from './hooks/useGetAgeGroups'
import { useEditVisitor } from './hooks/useEditVisitor'
import { useGetAgencies } from '../agencies/hooks/useGetAgencies'
import { useGetEmployees } from './hooks/useGetEmployees'
import { InventoryModelsMap, visitorFormSchema, VisitorSchema } from './schemas'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { GET_INVENTORY_QUERY_KEY, useGetInventory } from '../hooks/useGetInventory'
import { useUpdateInventory } from './hooks/useUpdateInventory'
import { routes } from '../routes'
import { GET_VISITOR_QUERY_KEY } from './hooks/useGetVisitor'
import { useCallback, useMemo, useState } from 'react'
import * as R from 'remeda'
import { ControlledAutocomplete } from '~/ui/components/ControlledAutocomplete'
import { countryList } from './constants'
import { Alert, Button, Chip, CircularProgress, ListItem, Stack, Typography } from '@mui/material'
import { ControlledTextField } from '~/ui/components/ControlledTextField'
import { FormWrapper } from '~/ui/components/FormWrapper'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { storeLocations } from '~/utils/constants'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { toast } from 'sonner'
import { useDeleteVisitor } from './hooks/useDeleteVisitor'
import { SimpleDialog } from '~/ui/components/SimpleDialog'
import { useValidateTotal } from './hooks/useValidateTotal'

const WOLLEM_GIFTS_AGENCY = 'wollem gifts'

export const EditVisitorForm = ({
  visitor,
  canDelete,
}: {
  visitor: Visitor
  canDelete: boolean
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showRefundDialog, setShowRefundDialog] = useState(false)

  const hasRefund = visitor.has_refund
  const isEditingSales = !hasRefund && visitor.total > 0

  const { data: agencies, isLoading: isAgenciesLoading } = useGetAgencies()
  const { data: employees, isLoading: isEmployeesLoading } = useGetEmployees()
  const { data: ageGroups, isLoading: ageGroupsLoading } = useGetAgeGroups()
  const { mutate: editVisitor, isPending: isEditingVisitor } = useEditVisitor()
  const { mutate: deleteVisitorMutation, isPending: isDeletingVisitor } = useDeleteVisitor()

  const isWollemGifts = visitor.agency.toLowerCase().includes(WOLLEM_GIFTS_AGENCY)

  const { mutateAsync: updateInventory, isPending: isUpdatingInventory } = useUpdateInventory()

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
  } = useForm<VisitorSchema>({
    defaultValues: visitor,
    resolver: zodResolver(
      visitorFormSchema.superRefine((data, ctx) => {
        if (!data.age_group) {
          ctx.addIssue({
            code: 'custom',
            path: ['age_group'],
            message: 'Age group is required',
          })
        }
      })
    ),
    mode: 'onChange',
  })

  const { data: inventory, isFetching: isLoadingModels } = useGetInventory({
    filters: [
      { column: 'location', operator: 'eq', value: watch('store') },
      { column: 'status', operator: 'in', value: ['available', 'sold'] },
    ],
  })

  const inventoryMap = useMemo<InventoryModelsMap>(
    () => R.mapToObj(inventory ?? [], (product) => [product.id.toString(), product]),
    [inventory]
  )

  const productIds = useMemo<string[]>(() => {
    if (!inventory) return []
    return inventory.filter((el) => el.status === 'available').map((el) => el.id.toString())
  }, [inventory])

  const handleUpdateVisitor = (data: VisitorSchema) => {
    editVisitor(
      {
        ...data,
        id: visitor.id,
        message_id: visitor.message_id,
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries({ queryKey: [GET_VISITOR_QUERY_KEY, visitor.id] })

          navigate(routes.sales.menu.visitors.route)
        },
      }
    )
  }

  const handleUpdateInventory = useCallback(
    async (formData: VisitorSchema) => {
      if (!formData.product_ids) return

      await updateInventory(
        formData.product_ids.map((id) => ({ id: Number(id), status: 'sold', sales: visitor.id })),
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
            toast.success('Inventory updated!')
          },
        }
      )
    },
    [queryClient, updateInventory, visitor.id]
  )

  const { ValidateTotalDialog, validateTotal } = useValidateTotal()

  // Products and total are locked for existing sales and gifts, so there is nothing to validate there
  const canEditTotal = !isEditingSales && !isWollemGifts

  const handleFormSubmit = (data: VisitorSchema, shouldCheckPriceAccuracy = true) => {
    if (shouldCheckPriceAccuracy && canEditTotal && data.product_ids && data.total !== 0) {
      const isTotalValid = validateTotal({
        onSubmit: (data) => handleFormSubmit(data, false),
        formData: data,
        modelsMapData: inventoryMap,
      })

      if (!isTotalValid) return
    }

    // only update inventory status when visitor has not taken the products yet
    // if the visitor entry is gifts that means the products are already sold, so we don't need to update the inventory
    if (!isEditingSales && !isWollemGifts && data.total > 0) {
      handleUpdateInventory(data).then(() => handleUpdateVisitor(data))
    } else handleUpdateVisitor(data)
  }

  const handleDelete = () => {
    const deleteVisitor = () =>
      deleteVisitorMutation(visitor.id, {
        onSuccess: () => {
          navigate(routes.sales.menu.visitors.route)
          setShowDeleteDialog(false)
        },
      })
    if (visitor.product_ids && visitor.product_ids.length > 0) {
      updateInventory(
        visitor.product_ids.map((id) => ({ id: Number(id), status: 'available', sales: null })),
        {
          onSuccess: () => {
            deleteVisitor()
          },
        }
      )
    } else deleteVisitor()
  }

  const handleRefund = () => {
    const refundSales = () =>
      editVisitor(
        { id: visitor.id, has_refund: true },
        {
          onSuccess: () => {
            navigate(routes.sales.menu.visitors.route)
            setShowRefundDialog(false)
          },
        }
      )
    if (visitor.product_ids && visitor.product_ids.length > 0) {
      updateInventory(
        visitor.product_ids.map((id) => ({ id: Number(id), status: 'available', sales: null })),
        {
          onSuccess: () => {
            refundSales()
          },
        }
      )
    } else refundSales()
  }

  const isLoading = isEditingVisitor || isUpdatingInventory || isDeletingVisitor

  // products is deprecated
  const products = watch('products')
  const productIdsFormData = watch('product_ids')
  const hasProducts = !!productIdsFormData?.length

  // this happens when a visitor is interested in one product, but before she comes back we sold it to another visitor.
  // When she comes back that product id is still in visitor data, but its not fetched and excluded from productIds
  const hasInvalidProducts = useMemo(() => {
    if (!visitor) return false
    if (!productIdsFormData) return false
    if (productIdsFormData.length === 0) return false

    return productIdsFormData.some((id) => !inventoryMap[id])
  }, [inventoryMap, productIdsFormData, visitor])

  const ageGroupOptions = ageGroups?.map(({ id }) => id) ?? []

  return (
    <Stack direction="row" overflow="hidden" flex={1}>
      {ValidateTotalDialog}
      <SimpleDialog
        open={showDeleteDialog}
        title="Delete Visitor"
        text="Are you sure you want to delete this visitor?"
        confirmText="Delete"
        onSubmit={handleDelete}
        onClose={() => setShowDeleteDialog(false)}
        color="error"
        isLoading={isDeletingVisitor}
      />
      <SimpleDialog
        open={showRefundDialog}
        title="Refund Sales"
        text="Are you sure you want to refund this sales?"
        confirmText="Refund"
        onSubmit={handleRefund}
        onClose={() => setShowRefundDialog(false)}
        isLoading={isEditingVisitor || isUpdatingInventory}
      />
      <FormWrapper backRoute={routes.sales.menu.visitors.route}>
        <Stack
          component="form"
          onSubmit={handleSubmit((data) => handleFormSubmit(data))}
          gap={2}
          sx={{ overflowY: 'auto' }}
          py={2}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <Controller
              control={control}
              name="created_at"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={new Date(field.value)}
                    onChange={(value) => {
                      field.onChange(value?.toISOString())
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                    disabled={hasRefund}
                  />
                </LocalizationProvider>
              )}
            />
            <ControlledAutocomplete
              errors={errors}
              control={control}
              name="store"
              label="Store"
              options={storeLocations}
              getOptionLabel={(value) => R.capitalize(value)}
              sx={{ flex: 1 }}
              disabled={isEditingSales}
              onChange={() => {
                if (hasProducts) {
                  toast.warning('Products are reset!', {
                    duration: 20000,
                    id: 'check-product-field',
                    description:
                      'You changed "Store" while having selected products in place. Make sure the "Products" field has correct values.',
                  })
                  reset({ ...watch(), product_ids: [] })
                }
              }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <ControlledAutocomplete
              errors={errors}
              control={control}
              name="employee"
              label="Employee"
              options={employees?.map(({ full_name }) => full_name) ?? []}
              loading={isEmployeesLoading}
              required
              sx={{ width: 0.5 }}
              disabled={hasRefund}
            />

            <ControlledAutocomplete
              control={control}
              name="country"
              errors={errors}
              label="Country"
              options={countryList}
              required
              sx={{ width: 0.5 }}
              disabled={hasRefund}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <ControlledAutocomplete
              label="Agency"
              options={
                agencies?.map(({ agency_name, agency_id }) => `${agency_name} - ${agency_id}`) ?? []
              }
              loading={isAgenciesLoading}
              control={control}
              name="agency"
              errors={errors}
              required
              // if its gifted then total field is dependent so disable editing to avoid complex logic
              disabled={isWollemGifts || hasRefund}
              sx={{ width: 0.5 }}
            />
            <ControlledAutocomplete
              label="Age group"
              options={ageGroupOptions}
              getOptionLabel={(id) => {
                const ageGroup = ageGroups?.find((el) => el.id === id)
                return ageGroup?.name ?? ''
              }}
              loading={ageGroupsLoading}
              control={control}
              name="age_group"
              errors={errors}
              required
              sx={{ width: 0.5 }}
              disabled={hasRefund}
            />
          </Stack>

          <ControlledTextField
            control={control}
            name="info"
            label="Info"
            multiline
            size="small"
            errors={errors}
            required
            disabled={hasRefund}
          />

          {products && products.length ? (
            <Stack gap={1}>
              <Stack direction="row" gap={1}>
                {products.map((p) => (
                  <Chip label={p} size="small" key={p} />
                ))}
              </Stack>

              <Typography color="warning" variant="caption">
                This entry is outdated.
              </Typography>
            </Stack>
          ) : (
            <ControlledAutocomplete
              errors={errors}
              label="Products"
              options={productIds}
              getOptionLabel={(id) => {
                const product = inventory?.find((el) => el.id.toString() === id)
                const label = product
                  ? `${product.models_v2.product_type === 'ring' ? `${product.stock_code_v2} - size ${product.size}` : product.stock_code_v2}`
                  : 'N/A'
                return label
              }}
              renderOption={(props, id) => {
                const product = inventory?.find((el) => el.id.toString() === id)
                return (
                  <ListItem {...props} key={product?.id}>
                    <Stack sx={{ gap: 0.5 }}>
                      <Typography variant="body2">{product?.stock_code_v2}</Typography>
                      <Stack direction="row" sx={{ gap: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Store : {product?.location}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          Size : {product?.size}
                        </Typography>
                      </Stack>
                    </Stack>
                  </ListItem>
                )
              }}
              loading={isLoadingModels}
              multiple
              control={control}
              name="product_ids"
              disabled={isEditingSales || isWollemGifts || hasRefund}
              filterSelectedOptions
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <ControlledTextField
              control={control}
              name="total"
              label="Total"
              size="small"
              errors={errors}
              fullWidth
              disabled={isEditingSales || !hasProducts || isWollemGifts || hasRefund}
              helperText="Add products to enable"
            />
            <ControlledTextField
              control={control}
              name="pax"
              label="Pax"
              size="small"
              errors={errors}
              required
              disabled={hasRefund}
            />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap>
            <Button
              variant="outlined"
              onClick={() => setShowDeleteDialog(true)}
              color="error"
              disabled={hasRefund || !canDelete}
            >
              Delete Visitor
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowRefundDialog(true)}
              disabled={hasRefund || !isEditingSales}
            >
              Refund sales
            </Button>
            <Button
              sx={{ flex: 1 }}
              type="submit"
              variant="contained"
              disabled={!isValid || isLoading || hasInvalidProducts || hasRefund}
              endIcon={isLoading ? <CircularProgress size={14} /> : null}
            >
              Update Visitor
            </Button>
          </Stack>
          {hasInvalidProducts ? (
            <Typography variant="body2" color="error">
              Please remove the invalid product from the form
            </Typography>
          ) : null}
          {hasRefund ? <Alert severity="warning">This sales has been refunded.</Alert> : null}
        </Stack>
      </FormWrapper>
    </Stack>
  )
}
